<template>
  <Modal
    ref="changeApproveStatusModalRef"
    @onNegativeActionClick="closeChangeApproveStatusModal"
    @onPositiveActionClick="changeProductsApproveStatus"
    @modalClosed="modalClosed"
    :isPositiveActionPending="isChangeApproveStatusLoading"
    :isPositiveActionButtonDisabled="
      isStatusInApproval
        ? false
        : isChangeApproveStatusLoading || !selectedModalStatus
    "
    negativeActionText="Vazgeç"
    positiveActionText="Onayla"
    :showIcon="isStatusInApproval"
    :modalStyle="
      isStatusInApproval
        ? { height: '338px', width: '464px' }
        : { width: '460px' }
    "
    :type="isStatusInApproval && 'info'"
    class="changeApproveStatusModal"
  >
    <template v-slot:body>
      <template v-if="!productStatus.includes('InApproval')">
        <h2 class="changeApproveStatusModal-title">Onay Durumunu Değiştir</h2>
        <RadioButtonGroup
          name="changeApproveStatusModal-radioGroup"
          class="changeApproveStatusModal-radioGroup"
          :options="productStatusOptions"
          @select="selectedModalStatus = $event"
        />
      </template>
      <template v-else>
        <h2 class="changeInApproveStatusModal-title">
          Ürün eşleşmelerini onaylıyor musunuz?
        </h2>
        <div class="changeInApproveStatusModal-text">
          Onaylamanız durumunda ürünleriniz katalogtaki bilgilerle ve girdiğiniz
          fiyat ile değiştirilecek. <br />
          Onaylıyor musunuz?
        </div>
      </template>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useStore } from "vuex";
import { Modal, RadioButtonGroup } from "@n11/so-ui-components";
import { sendProductsChangeApprovalStatusEvent } from "@/utils/ga4/helper";
import { ToastMessageTypes } from "@/utils/enum/toastMessageTypes";

const emit = defineEmits(["successChangeApproveStatus"]);

const props = defineProps({
  getSelectedRows: {
    type: Array,
    required: false,
    default: [],
  },
  tabParam: {
    type: String,
    required: false,
    default: "",
  },
  productStatus: {
    type: Array,
    required: false,
    default: [],
  },
});

const store = useStore();

const selectedModalStatus = ref();
const changeApproveStatusModalRef = ref();
const isChangeApproveStatusLoading = ref(false);
const productStatusOptions = [
  { id: "Active", name: "Onaylı" },
  { id: "Suspended", name: "Beklemede" },
];

const getShowProductStatusModal = computed(
  () => store.getters["products/getShowProductStatusModal"]
);

const openChangeApproveStatusModal = () =>
  changeApproveStatusModalRef?.value?.openModal();

const closeChangeApproveStatusModal = () =>
  changeApproveStatusModalRef?.value?.hiddenModal();

const isStatusInApproval = computed(() =>
  props.productStatus.includes("InApproval") ? true : false
);
const modalClosed = () => {
  store.commit("products/SET_SHOW_PRODUCT_STATUS_MODAL", null);
  selectedModalStatus.value = null;
};

const messageType = (data) => {
  const hasFailed = data?.failProductIds?.length > 0;
  const hasSucceeded = data?.successProductIds?.length > 0;

  if (hasFailed && hasSucceeded) {
    return {
      fail: ToastMessageTypes.ERROR,
      success: ToastMessageTypes.SUCCESS,
    };
  }
  if (hasFailed) {
    return ToastMessageTypes.ERROR;
  }
  if (hasSucceeded) {
    return ToastMessageTypes.SUCCESS;
  }
  return false;
};

const changeProductsApproveStatus = async () => {
  const pids = Object.keys(props.getSelectedRows);
  const selectedSingleItem = getShowProductStatusModal.value
    ? [String(getShowProductStatusModal.value)]
    : null;
  const tabParam = props.tabParam;
  const productStatus = props.productStatus;
  const payload = {
    productIds: selectedSingleItem ?? pids,
    productStatus: productStatus.includes("InApproval")
      ? (selectedModalStatus.value = "Active")
      : selectedModalStatus.value.id,
    ...(productStatus.includes("InApproval") && { tabParam }),
  };
  isChangeApproveStatusLoading.value = true;

  try {
    const res = await store.dispatch(
      "products/changeProductsApproveStatus",
      payload
    );
    emit("successChangeApproveStatus");
    const result = messageType(res.data);
    if (result !== false) {
      if (typeof result === "object") {
        store.commit("common/SET_TOASTER_DATA", {
          type: result.success,
          description: "Ürünlerin durum değişikliği başarılı.",
        });
        await new Promise((resolve) => setTimeout(resolve, 500));
        store.commit("common/SET_TOASTER_DATA", {
          type: result.fail,
          description: res.data.message,
        });
      } else {
        store.commit("common/SET_TOASTER_DATA", {
          type: result,
          description: res.data?.message,
        });
      }
    }
    sendProductsChangeApprovalStatusEvent({
      approval_status: selectedModalStatus.value.id,
      quantity: selectedSingleItem ? 1 : pids.length,
    });
  } catch (error) {
    console.error(error);
    store.commit("common/SET_TOASTER_DATA", {
      type: ToastMessageTypes.ERROR,
      title: ToastMessageTypes.ERROR_TITLE,
      description: ToastMessageTypes.ERROR_APPROVAL_STATUS_DESCRIPTION,
    });
  } finally {
    closeChangeApproveStatusModal();
    isChangeApproveStatusLoading.value = false;
  }
};

watch(getShowProductStatusModal, (newModalVal) => {
  if (newModalVal) {
    openChangeApproveStatusModal();
  } else {
    closeChangeApproveStatusModal();
  }
});

defineExpose({
  openChangeApproveStatusModal,
});
</script>

<style lang="scss" scoped>
.changeApproveStatusModal {
  &-title {
    font-size: 20px;
    font-weight: 700;
    text-align: left;
    margin-bottom: 12px;
  }

  &-radioGroup {
    margin-bottom: 12px;
    gap: 16px;
  }
}

.changeInApproveStatusModal {
  &-title {
    font-size: 20px;
    font-weight: 700;
    text-align: center;
    margin-bottom: 12px;
  }
  &-text {
    line-height: 24px;
    letter-spacing: -0.05px;
  }
}

.modal-text {
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: -0.05px;
}

.modal-title {
  font-size: 20px;
  font-weight: 700;
  line-height: 26px;
  letter-spacing: -0.2px;
}
</style>
